/**
 * channel-price-adapter.ts — 框架通道原生价格（A8 §10.3 / 字典 65 §9）的展示、校验与错误映射纯函数。
 *
 * 字段映射：`channel_prices_summary[]` / §10.3 `prices[]` 的 `currency`（ISO 4217）→ 币种码原样；
 * `amount_minor_units`（minor units 十进制串，上界 int8）→ 全程按字符串 / BigInt 处理，**不经 Number**
 * （超 2^53 丢精度），展示时按该币种 **ISO 4217** 小数位换算（`~/lib/iso4217-minor-units`，与 C 端发给 PayerMax 的
 * 换算同表；不用 Intl 的 CLDR 展示小数位，HUF / IDR 等两者差 100 倍）。
 *
 * 单币种：能力声明 `currency_rule` 闭集只有 `SINGLE_PRICE_ROW`（`PAYMENT_PROVIDER_CURRENCY_RULES`），
 * 每个框架通道商品行只有一行价格 —— UI 只给一个币种槽，改币种 = 替换这一行。
 */

import {
  type ChannelPriceSummaryItem,
  ISO_4217_CURRENT_CURRENCY_CODES,
  isPriceableCurrency,
} from '@novel-admin/shared';
import { AdminApiError } from '~/api/errors';
import { currencySymbol, fmtCurrencyMinor } from '~/lib/format';
import { iso4217MinorUnitExponent } from '~/lib/iso4217-minor-units';

/** int8 上界（与 shared `PutNativeChannelPricesRequestSchema` 同口径） */
const INT8_MAX = 9223372036854775807n;
const POSITIVE_INTEGER = /^[1-9][0-9]*$/;

/** 不可定价币种（存量行）的金额后缀：原样展示最小单位值，不换算主单位（字典 65 §9） */
const UNPRICEABLE_AMOUNT_SUFFIX = '（最小单位原值，币种不可定价）';

/** 主单位金额串（BigInt 换算，按 ISO 4217 小数位）；不可定价币种返回 null */
function majorAmount(price: ChannelPriceSummaryItem): string | null {
  const exponent = iso4217MinorUnitExponent(price.currency);
  if (exponent === null) return null;
  return fmtCurrencyMinor(price.amount_minor_units, price.currency, exponent);
}

/** 「EUR 2.99」：币种码 + 主单位金额（列表价格标签 / 确认弹窗明细用，等宽显示）*/
export function formatChannelPriceTag(price: ChannelPriceSummaryItem): string {
  const major = majorAmount(price);
  if (major === null)
    return `${price.currency} ${price.amount_minor_units}${UNPRICEABLE_AMOUNT_SUFFIX}`;
  return `${price.currency} ${major}`;
}

/** 「€2.99」：带货币符号的主单位金额（核对用）*/
export function formatChannelPriceMoney(price: ChannelPriceSummaryItem): string {
  const major = majorAmount(price);
  if (major === null)
    return `${price.currency} ${price.amount_minor_units}${UNPRICEABLE_AMOUNT_SUFFIX}`;
  return `${currencySymbol(price.currency)}${major}`;
}

/** 该币种的 ISO 4217 小数位（与 C 端换算同表）；不可定价币种为 null */
export function currencyMinorExponent(currency: string): number | null {
  return iso4217MinorUnitExponent(currency);
}

/** 最小单位的说明：「（EUR 以分计）」/「（JPY 无小数位）」/「（KWD 以 1/1000 主单位计）」*/
export function minorUnitHint(currency: string): string {
  const exp = currencyMinorExponent(currency);
  if (exp === null) return `（${currency} 不是可定价币种，无最小单位定义，请改选币种）`;
  if (exp === 0) return `（${currency} 无小数位，按主单位计）`;
  if (exp === 2) return `（${currency} 以分计）`;
  return `（${currency} 以 1/${10 ** exp} 主单位计）`;
}

/**
 * 金额输入校验：大于 0 的整数串、不超过 int8 上界。合法返回 null，否则返回给运营看的原因
 * （带「€3.49 应填 349」式示例，帮运营从主单位换算到最小单位）。
 */
export function amountMinorUnitsError(input: string, currency: string): string | null {
  const value = input.trim();
  if (value === '') return '请填写金额（最小单位）';
  const exp = currencyMinorExponent(currency);
  // 存量不可定价币种（编辑弹窗会把它补进选项）：没有最小单位定义，金额无从校验，先改币种
  if (exp === null) return `${currency} 不是可定价币种，请改选币种`;
  if (!POSITIVE_INTEGER.test(value)) {
    // 示例金额：EUR → 349（€3.49）；KWD → 3499（3.499）；无小数位的币种不给换算示例
    const sample = `3${'4'.padEnd(exp, '9')}`;
    const example =
      exp === 0
        ? `${currency} 无小数位，直接填整数金额`
        : `${formatChannelPriceMoney({ currency, amount_minor_units: sample })} 应填 ${sample}`;
    return `请输入大于 0 的整数，以 ${currency} 的最小单位计。${example}。`;
  }
  if (value.length > 19 || BigInt(value) > INT8_MAX) return '金额超出上限（int8）';
  return null;
}

/**
 * 可选币种：可定价币种（字典 65 §9），即 ISO 4217 现行码表去掉小数位为 N.A. 的码（XAU / XTS / XXX 等），判定取
 * shared `isPriceableCurrency`，与服务端写入校验同一份。不取 `Intl.supportedValuesOf`：ICU 含已退出 List One 的
 * 历史币种（如 BGN），选了也会被服务端拒。当前已存的币种不在列表里时由调用方补进选项，避免已有价格显示成空。
 */
export function currencyOptions(): string[] {
  return ISO_4217_CURRENT_CURRENCY_CODES.filter(isPriceableCurrency);
}

/** 列表行的价格摘要态：框架通道行 `[]` = 未定价；IAP / STRIPE 行（null）不展示价格 */
export type ChannelPriceSummaryState =
  | { kind: 'not_applicable' }
  | { kind: 'unpriced' }
  | { kind: 'priced'; prices: ChannelPriceSummaryItem[] };

export function channelPriceSummaryState(
  summary: ChannelPriceSummaryItem[] | null,
): ChannelPriceSummaryState {
  if (summary === null) return { kind: 'not_applicable' };
  if (summary.length === 0) return { kind: 'unpriced' };
  return { kind: 'priced', prices: summary };
}

/** 价格保存失败的展示：标题（运营可读）+ 可选补充 + 机读定位（code · field / reason）*/
export interface ChannelPriceErrorView {
  title: string;
  hint: string | null;
  code: string;
  /** 内容不变时重试应复用幂等键；`false` = 换新键（如 `IDEMPOTENCY_KEY_MISMATCH`）*/
  reuseIdempotencyKey: boolean;
  /** 行已不存在 / 已不是框架通道行：调用方刷新列表 */
  refreshList: boolean;
}

/** `prices.0.currency` / `prices[0].currency` → 末段字段名 */
function fieldTail(field: string): string {
  const parts = field.split(/[.[\]]+/).filter(Boolean);
  return parts[parts.length - 1] ?? field;
}

/**
 * A8 §10.3 PUT 的错误 → 运营提示。只按 `code` / `errors[].field` / `details.reason` 判，不按 message 文案匹配。
 */
export function describeChannelPriceError(
  err: unknown,
  channelName: string,
): ChannelPriceErrorView {
  if (!(err instanceof AdminApiError)) {
    return {
      title: err instanceof Error ? err.message : '保存失败',
      hint: '网络或服务异常，可直接重试（同一内容沿用同一幂等键，不会重复写入）',
      code: 'NETWORK',
      reuseIdempotencyKey: true,
      refreshList: false,
    };
  }
  const base = { reuseIdempotencyKey: true, refreshList: false };
  if (err.code === 'VALIDATION_FAILED') {
    const first = err.errors?.[0];
    const field = first?.field ?? '';
    const tail = fieldTail(field);
    const code = field ? `VALIDATION_FAILED · ${field}` : 'VALIDATION_FAILED';
    // 单币种超限与重复币种都落在 field=`prices`，契约不给机读区分（不按 message 文案猜），合并成一条
    if (field === 'prices') {
      return {
        ...base,
        title: `${channelName} 只能配置一个币种`,
        hint: '单币种规则：同一商品只有一行价格，同一币种也只能出现一次',
        code,
      };
    }
    if (tail === 'currency') {
      return {
        ...base,
        title: '币种须为可定价币种',
        hint: 'ISO 4217 现行码中有小数位定义的币种；XAU / XTS / XXX 等不可用',
        code,
      };
    }
    if (tail === 'amount_minor_units') {
      return {
        ...base,
        title: '金额须为大于 0 的整数（最小单位），且不超出上限',
        hint: null,
        code,
      };
    }
    if (tail === 'provider_price_ref') {
      return { ...base, title: '原生通道不接受价格引用', hint: '请刷新后重试', code };
    }
    return { ...base, title: err.message, hint: null, code };
  }
  if (err.code === 'RESOURCE_NOT_FOUND') {
    return {
      ...base,
      title: `商品不存在、已删除，或不是 ${channelName} 行`,
      hint: '已刷新列表。',
      code: 'RESOURCE_NOT_FOUND',
      refreshList: true,
    };
  }
  if (err.code === 'RESOURCE_CONFLICT') {
    const reason = typeof err.details?.reason === 'string' ? err.details.reason : '';
    if (reason === 'IDEMPOTENCY_KEY_MISMATCH') {
      return {
        title: '本次提交与上次未完成的提交内容不同',
        hint: '已换新的幂等键，请再点一次保存。',
        code: 'RESOURCE_CONFLICT · IDEMPOTENCY_KEY_MISMATCH',
        reuseIdempotencyKey: false,
        refreshList: false,
      };
    }
    if (reason === 'OPERATION_IN_PROGRESS') {
      return {
        ...base,
        title: '同一操作正在处理中',
        hint: '稍后再点保存，会沿用同一幂等键拿到结果。',
        code: 'RESOURCE_CONFLICT · OPERATION_IN_PROGRESS',
      };
    }
    return {
      ...base,
      title: err.message,
      hint: null,
      code: `RESOURCE_CONFLICT${reason ? ` · ${reason}` : ''}`,
    };
  }
  // 只有角色拒绝写成权限提示；MFA 未验证（AUTH_MFA_REQUIRED）等 AUTH_* 回退已按 code 本地化的文案
  if (err.code === 'FORBIDDEN_OPERATION') {
    return {
      ...base,
      title: '定价需要 root / admin 权限并完成 MFA',
      hint: null,
      code: err.code,
    };
  }
  return { ...base, title: err.message, hint: null, code: err.code };
}
